import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const AddManufacturedProductDialog = ({ open, onClose, onAdd, donor }) => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch EPL products when dialog opens
  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/products?organization=EPL&status=ACTIVE&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      
      // Filter for vial products (1.5ml and 3ml) - support both formats
      const vialProducts = data.data.filter(product => 
        product.volume && (
          product.volume.includes('1.5') || 
          product.volume.includes('3') ||
          product.volume.includes('1.5mL') ||
          product.volume.includes('3.0mL') ||
          product.volume.includes('3mL')
        )
      );
      
      setProducts(vialProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setSelectedProducts([...selectedProducts, {
      id: '',
      productId: '',
      productName: '',
      productSku: '',
      vialSize: '',
      quantity: 1,
      manufacturingDate: '',
      expirationDate: ''
    }]);
  };

  const handleRemoveProduct = (index) => {
    const updated = selectedProducts.filter((_, i) => i !== index);
    setSelectedProducts(updated);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...selectedProducts];
    
    if (field === 'productId') {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct) {
        updated[index] = {
          ...updated[index],
          productId: value,
          productName: selectedProduct.name,
          productSku: selectedProduct.sku,
          vialSize: selectedProduct.volume || ''
        };
      }
    } else {
      updated[index][field] = value;
    }
    
    setSelectedProducts(updated);
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedProducts.length === 0) {
      setError('Please add at least one product');
      return;
    }

    // Validate all products have required fields
    const invalidProducts = selectedProducts.filter(p => 
      !p.productId || !p.quantity || p.quantity <= 0 || !p.manufacturingDate || !p.expirationDate
    );

    if (invalidProducts.length > 0) {
      setError('Please fill in all required fields: Product, Vials Made, Manufacturing Date, and Expiration Date');
      return;
    }

    // Validate date formats
    const invalidDates = selectedProducts.filter(p => {
      const manDate = new Date(p.manufacturingDate);
      const expDate = new Date(p.expirationDate);
      return isNaN(manDate.getTime()) || isNaN(expDate.getTime());
    });

    if (invalidDates.length > 0) {
      setError('Please ensure all dates are valid');
      return;
    }

    // Validate expiration date is after manufacturing date
    const invalidDateOrder = selectedProducts.filter(p => {
      const manDate = new Date(p.manufacturingDate);
      const expDate = new Date(p.expirationDate);
      return expDate <= manDate;
    });

    if (invalidDateOrder.length > 0) {
      setError('Expiration date must be after manufacturing date');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Ensure dates are in ISO format and valid
      const manufacturingData = {
        vials: selectedProducts.map((p, index) => {
          // Validate dates are not empty
          if (!p.manufacturingDate || !p.expirationDate) {
            throw new Error(`Product ${index + 1} is missing required dates`);
          }
          
          // Format dates properly - ensure they're valid ISO strings
          const manDate = new Date(p.manufacturingDate);
          const expDate = new Date(p.expirationDate);
          
          // Validate dates are valid
          if (isNaN(manDate.getTime()) || isNaN(expDate.getTime())) {
            throw new Error(`Product ${index + 1} has invalid dates`);
          }
          
          return {
            productId: p.productId,
            quantity: parseInt(p.quantity),
            manufacturingDate: manDate.toISOString().split('T')[0], // YYYY-MM-DD format
            expirationDate: expDate.toISOString().split('T')[0] // YYYY-MM-DD format
          };
        })
      };

      console.log('Sending manufacturing data:', manufacturingData);
      await onAdd(manufacturingData);
      handleClose();
    } catch (err) {
      console.error('Error adding manufactured products:', err);
      setError(err.message || 'Failed to add manufactured products');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedProducts([]);
    setError('');
    onClose();
  };

  const getTotalVials = () => {
    return selectedProducts.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
  };


  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          <Typography variant="h6">Add Manufactured Products</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Donor: {donor?.donorId} - {donor?.mareName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Card */}
            {selectedProducts.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Total Products
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {selectedProducts.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Total Vials Made
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {getTotalVials()}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Add Product Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddProduct}
                disabled={products.length === 0}
              >
                Add Product
              </Button>
              
              {products.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <InfoIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  No EPL vial products found. Please ensure products are configured with 1.5mL or 3.0mL volumes.
                </Typography>
              )}
            </Box>

            {/* Selected Products */}
            {selectedProducts.map((selectedProduct, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" color="primary">
                      Product {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveProduct(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Product</InputLabel>
                        <Select
                          value={selectedProduct.productId}
                          onChange={(e) => handleProductChange(index, 'productId', e.target.value)}
                          label="Product"
                        >
                          {products.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {product.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.sku} • {product.volume}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        required
                        label="Vials Made"
                        type="number"
                        value={selectedProduct.quantity}
                        onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        required
                        label="Manufacturing Date"
                        type="date"
                        value={selectedProduct.manufacturingDate}
                        onChange={(e) => handleProductChange(index, 'manufacturingDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={6} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Expiration Date"
                        type="date"
                        value={selectedProduct.expirationDate}
                        onChange={(e) => handleProductChange(index, 'expirationDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    {selectedProduct.productId && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`SKU: ${selectedProduct.productSku}`} 
                            size="small" 
                            variant="outlined" 
                          />
                          <Chip 
                            label={`Size: ${selectedProduct.vialSize}`} 
                            size="small" 
                            variant="outlined" 
                          />
                          <Chip 
                            label={`Vials: ${selectedProduct.quantity || 0}`} 
                            size="small" 
                            color="primary" 
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {selectedProducts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No products added yet. Click "Add Product" to start.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || selectedProducts.length === 0 || loading}
          startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {submitting ? 'Adding...' : `Add ${selectedProducts.length} Product${selectedProducts.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddManufacturedProductDialog;